import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { CssBaseline } from "@mui/material";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { AuthProvider } from "./context/AuthContext";
import { AnalyticsProvider } from "./context/AnalyticsContext";
import { PageMetadataProvider } from "./context/PageMetadataContext";
import { ExperiencesProvider } from "./context/ExperiencesContext";
import { ContentProvider } from "./context/ContentContext";
import { initializeLocale, setLocale } from "./localization";
import LocalizedThemeProvider from "./localization/LocalizedThemeProvider";
import { LOCALE_STORAGE_KEY } from "./localization/locales";

initializeLocale();

const DARK_MODE_QUERY = "(prefers-color-scheme: dark)";

const getSystemColorMode = () => {
  if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    return window.matchMedia(DARK_MODE_QUERY).matches ? "dark" : "light";
  }
  return "light";
};

const syncDocumentTheme = (mode) => {
  if (typeof document === "undefined" || !document.documentElement) {
    return;
  }
  document.documentElement.setAttribute("data-theme", mode);
};

syncDocumentTheme(getSystemColorMode());

const RootProviders = () => {
  const [mode, setMode] = useState(getSystemColorMode);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === LOCALE_STORAGE_KEY && event.newValue) setLocale(event.newValue, { persist: false });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    syncDocumentTheme(mode);
  }, [mode]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const media = window.matchMedia(DARK_MODE_QUERY);
    const onChange = (event) => {
      const nextMode = event.matches ? "dark" : "light";
      syncDocumentTheme(nextMode);
      setMode(nextMode);
    };

    const initialMode = media.matches ? "dark" : "light";
    syncDocumentTheme(initialMode);
    setMode(initialMode);

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }

    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  return (
    <AuthProvider>
      <AnalyticsProvider>
        <PageMetadataProvider>
          <ExperiencesProvider>
            <ContentProvider>
              <LocalizedThemeProvider mode={mode}>
                <CssBaseline />
                <App />
              </LocalizedThemeProvider>
            </ContentProvider>
          </ExperiencesProvider>
        </PageMetadataProvider>
      </AnalyticsProvider>
    </AuthProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <RootProviders />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
